import { useState, useEffect } from 'react'
import { InputBase, Box, IconButton } from '@mui/material'
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material'
import { themeColors } from '../../app/layout/theme'

interface SearchBarProps {
  placeholder?: string
  onSearch?: (value: string) => void
  onChange?: (value: string) => void
  value?: string
  sx?: any
}

export const SearchBar = ({ placeholder = 'Search this list...', onSearch, onChange, value = '', sx }: SearchBarProps) => {
  const [focused, setFocused] = useState(false)
  const [localValue, setLocalValue] = useState(value)

  // Sync local state if value prop changes from parent externally
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const triggerChange = (val: string) => {
    if (onSearch) onSearch(val)
    if (onChange) onChange(val)
  }

  // Debounce query emission
  useEffect(() => {
    if (localValue === value) return

    const handler = setTimeout(() => {
      triggerChange(localValue)
    }, 400) // 400ms debounce delay

    return () => clearTimeout(handler)
  }, [localValue, onSearch, onChange, value])

  const handleClear = () => {
    setLocalValue('')
    triggerChange('') // Instant emission on clear for maximum responsiveness
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        px: '8px',
        height: '36px',
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: themeColors.surface,
        border: `1px solid ${focused ? themeColors.primary : themeColors.border}`,
        borderRadius: '8px',
        transition: 'all 0.15s ease',
        opacity: 1,
        ...sx,
      }}
    >
      <SearchIcon
        sx={{
          fontSize: 18,
          color: focused ? themeColors.primary : themeColors.text.muted,
          transition: 'color 0.15s',
          flexShrink: 0,
        }}
      />
      <InputBase
        value={localValue}
        placeholder={placeholder}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        sx={{
          flex: 1,
          fontSize: '13px',
          color: themeColors.text.primary,
          '& input::placeholder': { color: themeColors.text.muted, opacity: 1 },
        }}
      />
      {localValue && (
        <IconButton
          size="small"
          onClick={handleClear}
          sx={{
            p: 0.25,
            color: themeColors.text.muted,
            '&:hover': { color: themeColors.text.primary },
          }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  )
}
